// Objectives.tsx
import React, { useState, useEffect, useMemo } from "react";
import styles from "./Objectives.module.css";

// --- API Imports ---
import { fetchCourseOrderData } from "../../API/Course/CourseAPI";
import { fetchVocabularyWords } from "../../API/Vocabulary/VocabularyAPI";
import { callOpenAI } from "../../API/API";
import {
  buildSpanishTranslationPayload,
  buildGermanTranslationPayload,
  buildItalianTranslationPayload,
  buildFrenchTranslationPayload,
  buildPortugueseTranslationPayload,
  buildEnglishTranslationPayload,
} from "../../GenAIPrompts/GenAIPrompts";
import {
  fetchConversationObjectives,
  createConversationObjective,
  updateConversationObjective,
  deleteConversationObjective,
  ConversationObjective,
} from "../../API/Lesson/ObjectivesAPI";

// --- Language Constants ---
const AVAILABLE_LANGUAGES = ["English", "Spanish", "Italian", "French", "German", "Portuguese"];
const LANG_CODE_MAP: { [key: string]: string } = {
  English: "EN",
  Spanish: "ES",
  Italian: "IT",
  French: "FR",
  German: "DE",
  Portuguese: "PT",
};
const PROFICIENCY_LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2"];
const DIFFICULTIES = ["1", "2", "3"];
const DOMAINS = ["general", "construction", "medical", "business", "travel", "technical", "food"];

// --- Interface Definitions ---
interface CourseOrderAPIResponseItem {
  Identifier: string;
  Vocabulary_List: string;
}
interface CourseSectionProcessed {
  identifier: string;
  vocabularyListRaw: string;
  sectionNameForAPI: string;
}

interface EditModalFormState extends Partial<ConversationObjective> {}

const ConversationObjectives: React.FC = () => {
  // --- State ---
  const [sourceLanguage, setSourceLanguage] = useState<string>("");
  const [section, setSection] = useState<string>("");
  const [proficiencyLevel, setProficiencyLevel] = useState<string>(
    PROFICIENCY_LEVELS[2] // B1
  );
  const [difficulty, setDifficulty] = useState<string>(DIFFICULTIES[1]); // 2
  const [domain, setDomain] = useState<string>(DOMAINS[0]); // general

  const [courseSections, setCourseSections] = useState<CourseSectionProcessed[]>([]);
  const [existingObjectives, setExistingObjectives] = useState<ConversationObjective[]>([]);
  const [filteredObjectives, setFilteredObjectives] = useState<ConversationObjective[]>([]);
  const [baseLanguageFilter, setBaseLanguageFilter] = useState<string>("");
  const [vocabularyWords, setVocabularyWords] = useState<string[]>([]);
  const [selectedWordsBucket, setSelectedWordsBucket] = useState<Set<string>>(new Set());

  const [loadingExistingObjectives, setLoadingExistingObjectives] = useState<boolean>(false);
  const [existingObjectivesError, setExistingObjectivesError] = useState<string | null>(null);
  const [loadingSections, setLoadingSections] = useState<boolean>(false);
  const [loadingVocabulary, setLoadingVocabulary] = useState<boolean>(false);
  const [generatingObjectives, setGeneratingObjectives] = useState<boolean>(false);
  const [aiGeneratedObjectives, setAiGeneratedObjectives] = useState<any[] | null>(null);
  const [savingAiObjectives, setSavingAiObjectives] = useState<boolean>(false);

  const [error, setError] = useState<string | null>(null);
  const [generalStatusMessage, setGeneralStatusMessage] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingObjective, setEditingObjective] = useState<ConversationObjective | null>(null);
  const [editedObjectiveData, setEditedObjectiveData] = useState<EditModalFormState>({});

  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [newObjectiveData, setNewObjectiveData] = useState<Partial<ConversationObjective>>({
    wordId: "",
    objectiveBaseText: "",
    objectiveTargetText: "",
  });
  const [createModalSelectedWords, setCreateModalSelectedWords] = useState<Set<string>>(new Set());
  const [creatingObjective, setCreatingObjective] = useState<boolean>(false);

  // AI Edit Modal state
  const [showAIEditModal, setShowAIEditModal] = useState<boolean>(false);
  const [aiEditPrompt, setAIEditPrompt] = useState<string>("");
  const [aiEditPreview, setAIEditPreview] = useState<ConversationObjective[] | null>(null);
  const [isProcessingAIEdit, setIsProcessingAIEdit] = useState<boolean>(false);
  const [updateAllVariations, setUpdateAllVariations] = useState<boolean>(false);

  // Generate missing languages state
  const [generatingMissingLanguages, setGeneratingMissingLanguages] = useState<boolean>(false);

  // Delete all objectives state
  const [isDeleteAllModalOpen, setIsDeleteAllModalOpen] = useState<boolean>(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState<string>("");
  const [deletingAllObjectives, setDeletingAllObjectives] = useState<boolean>(false);

  // Generate all missing languages state
  const [generatingAllMissing, setGeneratingAllMissing] = useState<boolean>(false);

  const langCodeMap = useMemo(() => LANG_CODE_MAP, []);

  // --- Helper function to construct lang_pair_word_id ---
  const constructLangPairWordId = (targLangCode: string, baseLangCode: string, wordId?: string) => {
    if (!wordId) return undefined;
    // Extract just the word if it contains additional data
    const word = wordId.split(",")[0].trim();
    return `#Targ_${targLangCode}#Base_${baseLangCode}#${word}`;
  };

  // --- Helper function to extract word from lang_pair_word_id ---
  const extractWordFromLangPairId = (langPairWordId?: string): string => {
    if (!langPairWordId) return "";
    // Format: #Targ_ES#Base_EN#buenas tardes
    const parts = langPairWordId.split("#");
    // Return the last part (the actual word)
    return parts[parts.length - 1] || "";
  };

  // --- Load course sections when sourceLanguage changes ---
  useEffect(() => {
    async function loadCourseSectionsForLang() {
      setCourseSections([]);
      setSection("");
      setExistingObjectives([]);
      setExistingObjectivesError(null);
      setError(null);
      setGeneralStatusMessage(null);

      if (!sourceLanguage) return;
      setLoadingSections(true);
      try {
        const langCode = langCodeMap[sourceLanguage];
        if (!langCode) throw new Error(`Invalid source language: ${sourceLanguage}`);
        const rawCourseData: CourseOrderAPIResponseItem[] = await fetchCourseOrderData(langCode);
        const processedSections: CourseSectionProcessed[] = rawCourseData
          .map((item) => ({
            identifier: item.Identifier,
            vocabularyListRaw: item.Vocabulary_List,
            sectionNameForAPI: item.Vocabulary_List,
          }))
          .sort((a, b) => parseInt(a.identifier, 10) - parseInt(b.identifier, 10));
        setCourseSections(processedSections);
      } catch (err: any) {
        setError(`Failed to load course sections: ${err.message || "Unknown error"}`);
      } finally {
        setLoadingSections(false);
      }
    }
    loadCourseSectionsForLang();
  }, [sourceLanguage, langCodeMap]);

  // --- Load vocabulary and existing objectives when section changes ---
  useEffect(() => {
    async function loadSectionData() {
      setExistingObjectives([]);
      setExistingObjectivesError(null);
      setVocabularyWords([]);
      setSelectedWordsBucket(new Set());

      if (!section) return;

      // Load existing objectives
      setLoadingExistingObjectives(true);
      try {
        const data = await fetchConversationObjectives(section);
        setExistingObjectives(data);
      } catch (err: any) {
        setExistingObjectivesError(
          `Failed to load existing objectives for "${section}": ${err.message || "Unknown error"}`
        );
      } finally {
        setLoadingExistingObjectives(false);
      }

      // Load vocabulary words
      setLoadingVocabulary(true);
      try {
        const vocabData = await fetchVocabularyWords(section);
        const uniqueWords = Array.from(
          new Set(vocabData.map((w) => w.Targ_Word?.trim()).filter(Boolean))
        );
        setVocabularyWords(uniqueWords.sort());
      } catch (err: any) {
        console.error("Failed to load vocabulary:", err);
        setError(`Failed to load vocabulary: ${err.message || "Unknown error"}`);
      } finally {
        setLoadingVocabulary(false);
      }
    }
    loadSectionData();
  }, [section]);

  // --- Filter objectives by base language ---
  useEffect(() => {
    if (!baseLanguageFilter) {
      setFilteredObjectives(existingObjectives);
    } else {
      setFilteredObjectives(
        existingObjectives.filter((obj) => {
          // Support both new and legacy field names
          const baseLang = obj.base_lang_code || obj.baseLanguage;
          return baseLang === baseLanguageFilter;
        })
      );
    }
  }, [existingObjectives, baseLanguageFilter]);

  // --- Group objectives by word (extracted from lang_pair_word_id) ---
  interface GroupedObjective {
    word: string;
    targetLanguage: string;
    variations: ConversationObjective[];
  }

  const groupedObjectives = useMemo(() => {
    const groups: Record<string, ConversationObjective[]> = {};

    filteredObjectives.forEach((objective) => {
      // Extract the word from lang_pair_word_id, or fall back to legacy_word_id/wordId
      const word =
        extractWordFromLangPairId(objective.lang_pair_word_id) ||
        objective.legacy_word_id ||
        objective.wordId ||
        "Unknown";

      if (!groups[word]) {
        groups[word] = [];
      }
      groups[word].push(objective);
    });

    return Object.entries(groups)
      .map(([word, variations]) => ({
        word,
        targetLanguage: variations[0]?.targ_lang_code || variations[0]?.targetLanguage || "",
        variations: variations.sort((a, b) => {
          // Prioritize EN, then alphabetical
          const baseLangA = a.base_lang_code || a.baseLanguage || "";
          const baseLangB = b.base_lang_code || b.baseLanguage || "";
          if (baseLangA === "EN") return -1;
          if (baseLangB === "EN") return 1;
          return baseLangA.localeCompare(baseLangB);
        }),
      }))
      .sort((a, b) => a.word.localeCompare(b.word));
  }, [filteredObjectives]);

  // --- Handlers ---
  const handleSourceLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setSourceLanguage(e.target.value);

  const handleSectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSection(e.target.value);
    setGeneralStatusMessage(null);
    setError(null);
  };

  const handleProficiencyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setProficiencyLevel(e.target.value);
  const handleDifficultyChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setDifficulty(e.target.value);
  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => setDomain(e.target.value);

  const handleBaseLanguageFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setBaseLanguageFilter(e.target.value);

  // --- Handler for word selection ---
  const toggleWord = (word: string) => {
    setSelectedWordsBucket((prev) => {
      const next = new Set(prev);
      next.has(word) ? next.delete(word) : next.add(word);
      return next;
    });
  };

  // --- AI generation function ---
  const buildConversationObjectiveGenAiPayload = (
    selectedWords: string[],
    sourceLangCode: string,
    proficiencyLevel: string,
    difficulty: string,
    domain: string,
    section: string
  ) => {
    const systemContent = `
You are an AI assistant specialized in creating SHORT, DIRECT conversation learning objectives for language learning applications.

CRITICAL REQUIREMENTS:
- Objectives must be SHORT and DIRECT (maximum 3-4 words)
- Use simple action verbs like "Say", "Ask", "Order", "Greet", "Thank"
- Focus on ONE specific action per objective
- NO lengthy explanations or practice descriptions
- Use SPECIFIC pronouns: "he/him"
- Assume conversations are with a specific person, not generic interactions

Examples of GOOD objectives:
- For "Hola": "Say hello"
- For "Gracias": "Say thank you" 
- For "Agua": "Order water"
- For "¿Cómo estás?": "Ask how he is"
- For "¿Dónde vive?": "Ask where he lives"
- For "Adiós": "Say goodbye"

Examples of BAD objectives (too generic/long):
- "Ask how someone is" (use "Ask how he is" instead)
- "Practice greeting someone by saying 'Hola' and asking how they are"
- "Learn to express gratitude using 'Gracias' in various situations"

For each vocabulary word, create objectives that:
1. Are appropriate for ${proficiencyLevel} proficiency level
2. Have difficulty level ${difficulty} (1=easy, 2=medium, 3=hard)
3. Relate to the domain "${domain}"
4. Are contextually relevant to the section "${section}"
5. Use the vocabulary word in a practical conversation scenario

Output Format:
Return ONLY a valid JSON array with this exact structure:
[
  {
    "wordId": "vocabulary_word",
    "objectiveBaseText": "Short direct objective in English (max 4 words)",
    "objectiveTargetText": "Short direct objective in target language (max 4 words)",
    "tip": "ONE simple example response in the target language the learner could say to fulfill the objective (e.g., 'Un café con leche, por favor'). Must be an answer, not a restatement of the objective."
  }
]

IMPORTANT: Keep objectives concise and actionable. Focus on what the learner should DO, not what they should practice or learn.
`;

    const userPrompt = `
Generate SHORT, DIRECT conversation objectives for the following parameters:
- Target Language: ${sourceLangCode}
- Proficiency Level: ${proficiencyLevel}
- Difficulty: ${difficulty}
- Domain: ${domain}
- Section: ${section}
- Vocabulary Words: ${selectedWords.join(", ")}

For each vocabulary word, create ONE short objective (max 4 words) that uses a simple action verb.
Also include a "tip" — ONE simple example response the learner could say in the target language to fulfill the objective.
Focus on practical, real-world actions the learner can perform with a specific person.
Use "he/him" instead of "someone" or "they" to make objectives more concrete.

Examples:
- "Hola" → objective: "Say hello", tip: "¡Hola! ¿Cómo estás?"
- "Agua" → objective: "Order water", tip: "Un vaso de agua, por favor"
- "Gracias" → objective: "Say thank you", tip: "Muchas gracias por tu ayuda"
- "¿Cómo estás?" → objective: "Ask how he is", tip: "¿Cómo estás hoy?"

Return only the JSON array with no additional text or explanations.
`;

    return {
      messages: [
        { role: "system", content: systemContent.trim() },
        { role: "user", content: userPrompt.trim() },
      ],
      temperature: 0.7,
      max_tokens: 2000,
    };
  };

  // --- Translation payload mapping ---
  const getTranslationPayload = (
    baseLanguageName: string,
    objectiveJSON: string,
    sourceLangName: string
  ) => {
    switch (baseLanguageName) {
      case "Spanish":
        return buildSpanishTranslationPayload(objectiveJSON, sourceLangName);
      case "German":
        return buildGermanTranslationPayload(objectiveJSON, sourceLangName);
      case "Italian":
        return buildItalianTranslationPayload(objectiveJSON, sourceLangName);
      case "French":
        return buildFrenchTranslationPayload(objectiveJSON, sourceLangName);
      case "Portuguese":
        return buildPortugueseTranslationPayload(objectiveJSON, sourceLangName);
      case "English":
        return buildEnglishTranslationPayload(objectiveJSON, sourceLangName);
      default:
        return null;
    }
  };

  // --- Handler to generate AI objectives in multiple base languages ---
  const handleGenerateAIObjectives = async () => {
    if (!selectedWordsBucket.size || !section || !sourceLanguage) {
      setGeneralStatusMessage("Please select words and ensure section and language are chosen.");
      return;
    }

    setGeneratingObjectives(true);
    setGeneralStatusMessage("Generating AI conversation objectives for all base languages...");

    try {
      const targetLangCode = langCodeMap[sourceLanguage];
      const selectedWordsArray = Array.from(selectedWordsBucket);

      // Step 1: Generate original objectives in English
      const payload = buildConversationObjectiveGenAiPayload(
        selectedWordsArray,
        targetLangCode,
        proficiencyLevel,
        difficulty,
        domain,
        section
      );

      const aiResponse = await callOpenAI(payload);

      // Parse the response - it might be an object or a string
      let originalObjectives: any;
      try {
        if (typeof aiResponse?.response === "object" && aiResponse?.response !== null) {
          // Response is already an object
          originalObjectives = aiResponse.response;
        } else if (typeof aiResponse?.response === "string") {
          // Response is a string, need to parse it
          let jsonString = aiResponse.response;

          // Try to extract JSON if it's wrapped in text
          const arrayStartIndex = jsonString.indexOf("[");
          const arrayEndIndex = jsonString.lastIndexOf("]") + 1;

          if (arrayStartIndex !== -1 && arrayEndIndex > arrayStartIndex) {
            jsonString = jsonString.slice(arrayStartIndex, arrayEndIndex).trim();
          }

          originalObjectives = JSON.parse(jsonString);
        } else {
          throw new Error(`Unexpected response type: ${typeof aiResponse?.response}`);
        }
      } catch (parseError: any) {
        console.error("Failed to parse AI response:", parseError);
        console.error("AI response was:", aiResponse?.response);
        throw new Error(`Invalid response format from AI: ${parseError.message}`);
      }

      if (!Array.isArray(originalObjectives)) {
        throw new Error(`AI response is not an array. Got type: ${typeof originalObjectives}`);
      }

      // Validate that each objective has the required fields
      for (const obj of originalObjectives) {
        if (!obj.wordId || !obj.objectiveBaseText || !obj.objectiveTargetText) {
          console.error("Invalid objective structure:", obj);
          throw new Error(
            "AI generated objective is missing required fields (wordId, objectiveBaseText, or objectiveTargetText)"
          );
        }
      }

      const allGeneratedObjectives = [];

      // Step 2: Generate objectives for each base language
      for (const baseLanguage of AVAILABLE_LANGUAGES) {
        const baseLanguageCode = langCodeMap[baseLanguage];

        // Skip if base language is the same as target language
        // (e.g., don't create Portuguese → Portuguese objectives)
        if (baseLanguageCode === targetLangCode) {
          console.log(
            `Skipping ${baseLanguage} as it matches the target language ${sourceLanguage}`
          );
          continue;
        }

        setGeneralStatusMessage(`Generating objectives for ${baseLanguage} speakers...`);

        for (const originalObjective of originalObjectives) {
          let objectiveBaseText = originalObjective.objectiveBaseText;
          let objectiveTargetText = originalObjective.objectiveTargetText;

          // If base language is not English, translate the base text
          if (baseLanguage !== "English") {
            try {
              // Create a simple translation request for just the base text
              const translationPayload = {
                messages: [
                  {
                    role: "system",
                    content: `You are a professional translator. Translate the given English text to ${baseLanguage}. Keep it short and direct (max 4 words). Return only the translation with no additional text or formatting.`,
                  },
                  {
                    role: "user",
                    content: `Translate this short objective to ${baseLanguage}: "${originalObjective.objectiveBaseText}"`,
                  },
                ],
                temperature: 0.3,
                max_tokens: 50,
              };

              const translationResponse = await callOpenAI(translationPayload);

              // Handle both string and object responses
              let translatedText: string;
              if (typeof translationResponse?.response === "string") {
                translatedText = translationResponse.response.trim();
              } else if (
                typeof translationResponse?.response === "object" &&
                translationResponse?.response !== null
              ) {
                // If it's an object, try to extract the text
                translatedText = String(translationResponse.response).trim();
              } else {
                translatedText = originalObjective.objectiveBaseText;
              }

              objectiveBaseText = translatedText || originalObjective.objectiveBaseText;
              // Keep the target text the same since we want to practice the same target language
            } catch (translationErr: any) {
              console.error(`Failed to translate objective for ${baseLanguage}:`, translationErr);
              // Use original text as fallback
              objectiveBaseText = originalObjective.objectiveBaseText;
            }
          }

          const objectiveId = generateUUID();
          const langPairWordId = constructLangPairWordId(
            targetLangCode,
            baseLanguageCode,
            originalObjective.wordId
          );

          const objectiveForPreview = {
            sectionId: section,
            objectiveId,
            // New schema fields
            base_lang_code: baseLanguageCode,
            targ_lang_code: targetLangCode,
            level: section,
            lang_pair_word_id: langPairWordId,
            legacy_word_id: originalObjective.wordId,
            legacy_vocab_identifier: originalObjective.wordId,
            // Legacy fields for backward compatibility
            wordId: originalObjective.wordId || undefined,
            baseLanguage: baseLanguageCode, // Keep for backward compatibility
            targetLanguage: targetLangCode, // Keep for backward compatibility
            domain,
            proficiencyLevel,
            difficulty,
            objectiveBaseText,
            objectiveTargetText,
            tip: originalObjective.tip || "",
            createdAt: new Date().toISOString(),
          };

          allGeneratedObjectives.push(objectiveForPreview);
        }
      }

      setAiGeneratedObjectives(allGeneratedObjectives);
      setGeneralStatusMessage(
        `Generated ${allGeneratedObjectives.length} objectives for review. Please approve to save them.`
      );

      // Clear selected words
      setSelectedWordsBucket(new Set());
    } catch (err: any) {
      console.error("Failed to generate AI objectives:", err);
      setGeneralStatusMessage(`Error generating AI objectives: ${err.message}`);
    } finally {
      setGeneratingObjectives(false);
    }
  };

  // --- Handler to approve and save AI-generated objectives ---
  const handleApproveAiObjectives = async () => {
    if (!aiGeneratedObjectives) return;

    setSavingAiObjectives(true);
    setGeneralStatusMessage("Saving approved objectives...");

    try {
      let savedCount = 0;
      for (const objective of aiGeneratedObjectives) {
        await createConversationObjective(objective);
        savedCount++;
      }

      setGeneralStatusMessage(`Successfully saved ${savedCount} AI-generated objectives.`);

      // Refetch objectives to show updated list
      try {
        const updatedObjectives = await fetchConversationObjectives(section);
        setExistingObjectives(updatedObjectives);
      } catch (fetchErr: any) {
        console.error("Failed to refetch objectives after saving:", fetchErr);
      }

      // Clear AI generated content
      setAiGeneratedObjectives(null);
    } catch (err: any) {
      console.error("Failed to save AI objectives:", err);
      setGeneralStatusMessage(`Error saving objectives: ${err.message}`);
    } finally {
      setSavingAiObjectives(false);
    }
  };

  // --- Handler to reject AI-generated objectives ---
  const handleRejectAiObjectives = () => {
    setAiGeneratedObjectives(null);
    setGeneralStatusMessage("AI-generated objectives discarded.");
  };

  // --- Handler to delete an existing objective ---
  const handleDeleteExistingObjective = async (
    objectiveToDelete: ConversationObjective,
    allVariations: ConversationObjective[]
  ) => {
    // Extract the word from the objective
    const word =
      extractWordFromLangPairId(objectiveToDelete.lang_pair_word_id) ||
      objectiveToDelete.legacy_word_id ||
      objectiveToDelete.wordId ||
      "Unknown";

    // If there's only one variation, just delete it without asking
    if (allVariations.length === 1) {
      if (
        !window.confirm(
          `Are you sure you want to delete this objective: "${objectiveToDelete.objectiveTargetText}"?`
        )
      )
        return;

      setGeneralStatusMessage(`Deleting objective ${objectiveToDelete.objectiveId}...`);
      try {
        await deleteConversationObjective(
          objectiveToDelete.sectionId,
          objectiveToDelete.objectiveId
        );
        setGeneralStatusMessage(`Objective ${objectiveToDelete.objectiveId} deleted successfully.`);
        setExistingObjectives((prev) =>
          prev.filter((obj) => obj.objectiveId !== objectiveToDelete.objectiveId)
        );
      } catch (err: any) {
        console.error("Failed to delete objective:", err);
        setGeneralStatusMessage(
          `Error deleting objective ${objectiveToDelete.objectiveId}: ${err.message}`
        );
      }
      return;
    }

    // If there are multiple variations, ask which ones to delete
    const baseLang = objectiveToDelete.base_lang_code || objectiveToDelete.baseLanguage;
    const choice = window.confirm(
      `This word "${word}" has ${allVariations.length} base language variation(s).\n\n` +
        `Click OK to delete ALL ${allVariations.length} variations.\n` +
        `Click Cancel to delete ONLY the current variation (${baseLang}).`
    );

    if (choice) {
      // Delete all variations
      const confirmAll = window.confirm(
        `Are you sure you want to delete ALL ${allVariations.length} variations for "${word}"?\n\nThis action cannot be undone.`
      );

      if (!confirmAll) return;

      setGeneralStatusMessage(`Deleting all ${allVariations.length} variations for "${word}"...`);

      try {
        let deletedCount = 0;
        for (const variation of allVariations) {
          await deleteConversationObjective(variation.sectionId, variation.objectiveId);
          deletedCount++;
        }

        setGeneralStatusMessage(
          `Successfully deleted all ${deletedCount} variation(s) for "${word}".`
        );

        // Remove all variations from state
        const deletedIds = new Set(allVariations.map((v) => v.objectiveId));
        setExistingObjectives((prev) => prev.filter((obj) => !deletedIds.has(obj.objectiveId)));
      } catch (err: any) {
        console.error("Failed to delete all variations:", err);
        setGeneralStatusMessage(`Error deleting variations: ${err.message}`);
      }
    } else {
      // Delete only the current variation
      const confirmSingle = window.confirm(
        `Are you sure you want to delete this specific variation (${baseLang})?\n\n"${objectiveToDelete.objectiveTargetText}"`
      );

      if (!confirmSingle) return;

      setGeneralStatusMessage(`Deleting objective ${objectiveToDelete.objectiveId}...`);

      try {
        await deleteConversationObjective(
          objectiveToDelete.sectionId,
          objectiveToDelete.objectiveId
        );
        setGeneralStatusMessage(
          `Objective deleted successfully (${baseLang} variation for "${word}").`
        );
        setExistingObjectives((prev) =>
          prev.filter((obj) => obj.objectiveId !== objectiveToDelete.objectiveId)
        );
      } catch (err: any) {
        console.error("Failed to delete objective:", err);
        setGeneralStatusMessage(
          `Error deleting objective ${objectiveToDelete.objectiveId}: ${err.message}`
        );
      }
    }
  };

  // --- Handler for opening the Edit Modal ---
  const handleOpenEditModal = (objective: ConversationObjective) => {
    setEditingObjective(objective);
    setEditedObjectiveData({ ...objective });
    setIsEditModalOpen(true);
    setGeneralStatusMessage(null);
  };

  // --- Handler to close the Edit Modal ---
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingObjective(null);
    setEditedObjectiveData({});
  };

  // --- Handler for any field changes in the Edit Modal ---
  const handleEditFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditedObjectiveData((prev) => ({ ...prev, [name]: value }));
  };

  // --- Handler to actually send updates from the Edit Modal to the backend ---
  const handleUpdateEditedObjective = async () => {
    if (!editingObjective || Object.keys(editedObjectiveData).length === 0) {
      setGeneralStatusMessage("Error: No objective data to update or no changes made.");
      return;
    }
    setGeneralStatusMessage(`Updating objective ${editingObjective.objectiveId}...`);
    try {
      // Include new schema fields if they're being updated
      const dataToUpdate = { ...editedObjectiveData };

      // If wordId is being updated, also update the lang_pair_word_id
      if (dataToUpdate.wordId !== undefined) {
        const baseLang = editingObjective.base_lang_code || editingObjective.baseLanguage || "EN";
        const targLang =
          editingObjective.targ_lang_code ||
          editingObjective.targetLanguage ||
          langCodeMap[sourceLanguage];
        dataToUpdate.lang_pair_word_id = constructLangPairWordId(
          targLang,
          baseLang,
          dataToUpdate.wordId
        );
        dataToUpdate.legacy_word_id = dataToUpdate.wordId;
        dataToUpdate.legacy_vocab_identifier = dataToUpdate.wordId;
      }

      await updateConversationObjective(
        editingObjective.sectionId,
        editingObjective.objectiveId,
        dataToUpdate
      );
      setGeneralStatusMessage(`Objective ${editingObjective.objectiveId} updated successfully.`);

      // Refetch the objectives to get the updated data
      try {
        const updatedObjectives = await fetchConversationObjectives(section);
        setExistingObjectives(updatedObjectives);
      } catch (fetchErr: any) {
        console.error("Failed to refetch objectives after update:", fetchErr);
        // Still close the modal even if refetch fails
      }

      handleCloseEditModal();
    } catch (err: any) {
      console.error("Failed to update objective:", err);
      setGeneralStatusMessage(
        `Error updating objective ${editingObjective.objectiveId}: ${err.message}`
      );
    }
  };

  // --- Handler for opening the Create Modal ---
  const handleOpenCreateModal = () => {
    if (!section) {
      setGeneralStatusMessage("Please select a section first.");
      return;
    }
    setNewObjectiveData({
      wordId: "",
      objectiveBaseText: "",
      objectiveTargetText: "",
    });
    setCreateModalSelectedWords(new Set());
    setIsCreateModalOpen(true);
    setGeneralStatusMessage(null);
  };

  // --- Handler to close the Create Modal ---
  const handleCloseCreateModal = () => {
    setIsCreateModalOpen(false);
    setNewObjectiveData({
      wordId: "",
      objectiveBaseText: "",
      objectiveTargetText: "",
    });
    setCreateModalSelectedWords(new Set());
  };

  // --- Handler for word selection in create modal ---
  const toggleCreateModalWord = (word: string) => {
    setCreateModalSelectedWords((prev) => {
      const next = new Set(prev);
      next.has(word) ? next.delete(word) : next.add(word);
      return next;
    });
  };

  // --- Handler for any field changes in the Create Modal ---
  const handleCreateFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setNewObjectiveData((prev) => ({ ...prev, [name]: value }));
  };

  // --- Helper function to generate UUID ---
  const generateUUID = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // --- Handler to create new objective ---
  const handleCreateNewObjective = async () => {
    if (!newObjectiveData.objectiveBaseText || !newObjectiveData.objectiveTargetText) {
      setGeneralStatusMessage("Error: Base text and target text are required.");
      return;
    }
    if (!section || !sourceLanguage) {
      setGeneralStatusMessage("Error: Section and source language must be selected.");
      return;
    }

    setCreatingObjective(true);
    setGeneralStatusMessage("Creating new objective...");

    try {
      const langCode = langCodeMap[sourceLanguage];
      const selectedWordsString =
        createModalSelectedWords.size > 0
          ? Array.from(createModalSelectedWords).join(", ")
          : undefined;

      const objectiveId = generateUUID();
      const baseLangCode = baseLanguageFilter || "EN";
      const langPairWordId = constructLangPairWordId(langCode, baseLangCode, selectedWordsString);

      const objectiveToCreate = {
        sectionId: section,
        objectiveId,
        // New schema fields
        base_lang_code: baseLangCode,
        targ_lang_code: langCode,
        level: section,
        lang_pair_word_id: langPairWordId,
        legacy_word_id: selectedWordsString,
        legacy_vocab_identifier: selectedWordsString,
        // Legacy fields for backward compatibility
        wordId: selectedWordsString,
        baseLanguage: baseLangCode, // Keep for backward compatibility
        targetLanguage: langCode, // Keep for backward compatibility
        domain,
        proficiencyLevel,
        difficulty,
        objectiveBaseText: newObjectiveData.objectiveBaseText,
        objectiveTargetText: newObjectiveData.objectiveTargetText,
      };

      await createConversationObjective(objectiveToCreate);
      setGeneralStatusMessage("Objective created successfully.");

      // Refetch the objectives to get the newly created one
      try {
        const updatedObjectives = await fetchConversationObjectives(section);
        setExistingObjectives(updatedObjectives);
      } catch (fetchErr: any) {
        console.error("Failed to refetch objectives after creation:", fetchErr);
        // Still close the modal even if refetch fails
      }

      handleCloseCreateModal();
    } catch (err: any) {
      console.error("Failed to create objective:", err);
      setGeneralStatusMessage(`Error creating objective: ${err.message}`);
    } finally {
      setCreatingObjective(false);
    }
  };

  // ── AI Editing Handlers ──
  const handleOpenAIEdit = (objective: ConversationObjective) => {
    setEditingObjective(objective);
    setShowAIEditModal(true);
    setAIEditPrompt("");
    setAIEditPreview(null);
    setUpdateAllVariations(false);
  };

  const handleCloseAIEdit = () => {
    setShowAIEditModal(false);
    setAIEditPrompt("");
    setAIEditPreview(null);
    setEditingObjective(null);
  };

  const handleProcessAIEdit = async () => {
    if (!aiEditPrompt.trim() || !editingObjective) {
      alert("Please enter modification instructions");
      return;
    }

    setIsProcessingAIEdit(true);
    setGeneralStatusMessage("Generating AI modifications for all languages...");

    try {
      // Get the actual languages from the objective being edited
      const currentBaseLang =
        editingObjective.base_lang_code || editingObjective.baseLanguage || "EN";
      const targLang = editingObjective.targ_lang_code || editingObjective.targetLanguage;

      // Get the language names for clearer prompts
      const currentBaseLangName =
        Object.keys(langCodeMap).find((name) => langCodeMap[name] === currentBaseLang) ||
        currentBaseLang;
      const targLangName =
        Object.keys(langCodeMap).find((name) => langCodeMap[name] === targLang) || targLang;

      // Step 1: Generate the modified objective - output base in English, target in actual target language
      const systemContent = `
You are an AI assistant specialized in modifying conversation learning objectives for language learning applications.

CRITICAL RULES (in priority order):
1. FOLLOW THE USER'S MODIFICATION INSTRUCTIONS EXACTLY - this is the highest priority
2. If the user provides exact wording in quotes or says "change this to say [exact phrase]", use that EXACT wording even if it's longer than 3-4 words
3. If the user's exact wording is longer, DO NOT shorten it - use their full phrase
4. Only apply the 3-4 word guideline when the user gives general modification instructions (like "make it more formal") rather than exact wording
5. Use simple action verbs like "Say", "Ask", "Order", "Greet", "Thank" when appropriate
6. Focus on ONE specific action per objective
7. When creating new objectives (not modifying), use specific pronouns "he/him/she/her" instead of generic "someone"
8. When the user specifies pronouns in their instructions (like "I", "me", "you"), preserve those pronouns exactly
9. Ensure objectives remain natural and grammatically correct
10. Maintain appropriate difficulty and proficiency level

IMPORTANT OUTPUT REQUIREMENTS:
- objectiveBaseText: MUST be in ENGLISH (we will translate to other languages later)
- objectiveTargetText: MUST be in ${targLangName} (${targLang})

Output Format:
Respond ONLY with a valid JSON object:
{
  "objectiveBaseText": "The modified objective in ENGLISH",
  "objectiveTargetText": "The modified objective in ${targLangName}"
}`;

      const userContent = `
CURRENT OBJECTIVE TO MODIFY:
The current base text is in ${currentBaseLangName} (${currentBaseLang}): "${editingObjective.objectiveBaseText}"
The current target text is in ${targLangName} (${targLang}): "${editingObjective.objectiveTargetText}"
Word/Phrase: "${extractWordFromLangPairId(editingObjective.lang_pair_word_id) || editingObjective.wordId || "N/A"}"

Context:
- Learners are learning: ${targLangName} (${targLang})
- Domain: ${editingObjective.domain}
- Proficiency Level: ${editingObjective.proficiencyLevel}
- Difficulty: ${editingObjective.difficulty}

USER'S MODIFICATION REQUEST:
${aiEditPrompt}

Apply the user's modification request to create the new objective.
REMEMBER: Output objectiveBaseText in ENGLISH and objectiveTargetText in ${targLangName}.
Return the result in the exact JSON format specified above.`;

      const payload = {
        messages: [
          {
            role: "system",
            content: systemContent,
          },
          {
            role: "user",
            content: userContent,
          },
        ],
        temperature: 0.7,
        max_tokens: 500,
        json_mode: true,
      };

      console.log("Sending AI edit request for English version:", payload);

      const result = await callOpenAI(payload);
      console.log("AI edit response:", result);

      if (!result) {
        throw new Error("No response received from AI");
      }

      // Parse the response
      let englishVersion: any;
      try {
        if (typeof result.response === "object" && result.response !== null) {
          englishVersion = result.response;
        } else if (typeof result.response === "string") {
          let jsonString = result.response;
          const startIndex = jsonString.indexOf("{");
          const endIndex = jsonString.lastIndexOf("}") + 1;
          if (startIndex !== -1 && endIndex > startIndex) {
            jsonString = jsonString.slice(startIndex, endIndex).trim();
            englishVersion = JSON.parse(jsonString);
          } else {
            throw new Error("No valid JSON found in response");
          }
        } else {
          throw new Error(`Unexpected response type: ${typeof result.response}`);
        }
      } catch (parseError: any) {
        console.error("Failed to parse AI response:", parseError);
        throw new Error(`Invalid response format from AI: ${parseError.message}`);
      }

      // Step 2: Generate translations for all base languages
      const allPreviews: ConversationObjective[] = [];

      for (const baseLanguage of AVAILABLE_LANGUAGES) {
        const baseLanguageCode = langCodeMap[baseLanguage];

        // Skip if base language is the same as target language
        // (e.g., don't create Spanish → Spanish objectives)
        if (baseLanguageCode === targLang) {
          console.log(`Skipping ${baseLanguage} as it matches the target language ${targLang}`);
          continue;
        }

        setGeneralStatusMessage(`Translating objective for ${baseLanguage} speakers...`);

        let objectiveBaseText = englishVersion.objectiveBaseText;
        const objectiveTargetText = englishVersion.objectiveTargetText;

        // If base language is not English, translate the base text
        if (baseLanguage !== "English") {
          try {
            const translationPayload = {
              messages: [
                {
                  role: "system",
                  content: `You are a professional translator. Translate the given English text to ${baseLanguage}. Keep it short and direct (max 4 words). Return only the translation with no additional text or formatting.`,
                },
                {
                  role: "user",
                  content: `Translate this short objective to ${baseLanguage}: "${englishVersion.objectiveBaseText}"`,
                },
              ],
              temperature: 0.3,
              max_tokens: 50,
            };

            const translationResponse = await callOpenAI(translationPayload);

            // Handle both string and object responses
            let translatedText: string;
            if (typeof translationResponse?.response === "string") {
              translatedText = translationResponse.response.trim();
            } else if (
              typeof translationResponse?.response === "object" &&
              translationResponse?.response !== null
            ) {
              // If it's an object, try to extract the text
              translatedText = String(translationResponse.response).trim();
            } else {
              translatedText = englishVersion.objectiveBaseText;
            }

            objectiveBaseText = translatedText || englishVersion.objectiveBaseText;
          } catch (translationErr: any) {
            console.error(`Failed to translate objective for ${baseLanguage}:`, translationErr);
            objectiveBaseText = englishVersion.objectiveBaseText;
          }
        }

        // Create preview for this language
        const preview: ConversationObjective = {
          ...editingObjective,
          base_lang_code: baseLanguageCode,
          baseLanguage: baseLanguageCode,
          targ_lang_code: targLang,
          targetLanguage: targLang,
          objectiveBaseText,
          objectiveTargetText,
        };

        allPreviews.push(preview);
      }

      setAIEditPreview(allPreviews);
      setGeneralStatusMessage(
        `Generated ${allPreviews.length} language variations. Review and apply changes.`
      );
      console.log("AI edit previews created for all languages:", allPreviews);
    } catch (err: any) {
      console.error("Error processing AI edit:", err);
      setGeneralStatusMessage(null);
      alert(`Failed to process AI edit: ${err.message || "Unknown error"}`);
    } finally {
      setIsProcessingAIEdit(false);
    }
  };

  const handleApplyAIEdit = async () => {
    if (!aiEditPreview || !editingObjective) return;

    try {
      setGeneralStatusMessage("Applying AI changes...");

      if (updateAllVariations) {
        // Find all variations (objectives with the same word)
        const word =
          extractWordFromLangPairId(editingObjective.lang_pair_word_id) ||
          editingObjective.legacy_word_id ||
          editingObjective.wordId ||
          "Unknown";

        const variations = existingObjectives.filter((obj) => {
          const objWord =
            extractWordFromLangPairId(obj.lang_pair_word_id) || obj.legacy_word_id || obj.wordId;
          return objWord === word;
        });

        // Update each variation with the corresponding preview for its base language
        await Promise.all(
          variations.map((variation) => {
            const baseLang = variation.base_lang_code || variation.baseLanguage;
            // Find the matching preview for this base language
            const matchingPreview = aiEditPreview.find(
              (preview) => (preview.base_lang_code || preview.baseLanguage) === baseLang
            );

            if (matchingPreview) {
              const updatedVariation: Partial<ConversationObjective> = {
                objectiveBaseText: matchingPreview.objectiveBaseText,
                objectiveTargetText: matchingPreview.objectiveTargetText,
              };
              return updateConversationObjective(
                variation.sectionId,
                variation.objectiveId,
                updatedVariation
              );
            }
            return Promise.resolve();
          })
        );

        // Refetch objectives to update local state
        const updatedObjectives = await fetchConversationObjectives(section);
        setExistingObjectives(updatedObjectives);

        handleCloseAIEdit();
        setGeneralStatusMessage(
          `Successfully updated ${variations.length} variation(s) with AI changes!`
        );
        setTimeout(() => setGeneralStatusMessage(null), 3000);
      } else {
        // Update only the single objective that was being edited
        const editingBaseLang = editingObjective.base_lang_code || editingObjective.baseLanguage;
        const matchingPreview = aiEditPreview.find(
          (preview) => (preview.base_lang_code || preview.baseLanguage) === editingBaseLang
        );

        if (matchingPreview) {
          const updateData: Partial<ConversationObjective> = {
            objectiveBaseText: matchingPreview.objectiveBaseText,
            objectiveTargetText: matchingPreview.objectiveTargetText,
          };

          await updateConversationObjective(
            editingObjective.sectionId,
            editingObjective.objectiveId,
            updateData
          );

          // Refetch objectives to update local state
          const updatedObjectives = await fetchConversationObjectives(section);
          setExistingObjectives(updatedObjectives);

          handleCloseAIEdit();
          setGeneralStatusMessage("Objective updated successfully with AI changes!");
          setTimeout(() => setGeneralStatusMessage(null), 3000);
        } else {
          throw new Error("Could not find matching preview for the selected base language");
        }
      }
    } catch (err: any) {
      console.error("Error applying AI edit:", err);
      setGeneralStatusMessage(null);
      alert(`Failed to apply AI edit: ${err.message || "Unknown error"}`);
    }
  };

  // --- Handler to generate missing language variations ---
  const handleGenerateMissingLanguages = async (variations: ConversationObjective[]) => {
    if (!variations || variations.length === 0) return;

    setGeneratingMissingLanguages(true);
    setGeneralStatusMessage("Generating missing language variations...");

    try {
      // Get the target language from the first variation
      const firstVariation = variations[0];
      const targLang = firstVariation.targ_lang_code || firstVariation.targetLanguage;
      const targLangCode = langCodeMap[sourceLanguage] || targLang;

      if (!targLangCode) {
        throw new Error("Could not determine target language code");
      }

      // Find which base languages already exist
      const existingBaseLangs = new Set(variations.map((v) => v.base_lang_code || v.baseLanguage));

      // Find missing base languages (excluding the target language itself)
      const missingBaseLangs = AVAILABLE_LANGUAGES.map((lang) => langCodeMap[lang]).filter(
        (langCode) => langCode !== targLangCode && !existingBaseLangs.has(langCode)
      );

      if (missingBaseLangs.length === 0) {
        setGeneralStatusMessage("All language variations already exist!");
        setGeneratingMissingLanguages(false);
        return;
      }

      // Use English version as the base, or the first available variation
      const englishVariation =
        variations.find((v) => (v.base_lang_code || v.baseLanguage) === "EN") || variations[0];

      const word =
        extractWordFromLangPairId(englishVariation.lang_pair_word_id) ||
        englishVariation.legacy_word_id ||
        englishVariation.wordId;

      const newObjectives: ConversationObjective[] = [];

      // Generate objectives for each missing base language
      for (const missingBaseLangCode of missingBaseLangs) {
        const missingBaseLangName =
          Object.keys(langCodeMap).find((name) => langCodeMap[name] === missingBaseLangCode) ||
          missingBaseLangCode;

        setGeneralStatusMessage(`Generating ${missingBaseLangName} variation...`);

        let objectiveBaseText = englishVariation.objectiveBaseText;
        const objectiveTargetText = englishVariation.objectiveTargetText;

        // If base language is not English, translate the base text
        if (missingBaseLangCode !== "EN") {
          try {
            const translationPayload = {
              messages: [
                {
                  role: "system",
                  content: `You are a professional translator. Translate the given English text to ${missingBaseLangName}. Keep it short and direct. Return only the translation with no additional text or formatting.`,
                },
                {
                  role: "user",
                  content: `Translate this objective to ${missingBaseLangName}: "${englishVariation.objectiveBaseText}"`,
                },
              ],
              temperature: 0.3,
              max_tokens: 100,
            };

            const translationResponse = await callOpenAI(translationPayload);

            let translatedText: string;
            if (typeof translationResponse?.response === "string") {
              translatedText = translationResponse.response.trim();
            } else if (
              typeof translationResponse?.response === "object" &&
              translationResponse?.response !== null
            ) {
              translatedText = String(translationResponse.response).trim();
            } else {
              translatedText = englishVariation.objectiveBaseText;
            }

            objectiveBaseText = translatedText || englishVariation.objectiveBaseText;
          } catch (translationErr: any) {
            console.error(
              `Failed to translate objective for ${missingBaseLangName}:`,
              translationErr
            );
            objectiveBaseText = englishVariation.objectiveBaseText;
          }
        }

        const objectiveId = generateUUID();
        const langPairWordId = constructLangPairWordId(targLangCode, missingBaseLangCode, word);

        const newObjective: ConversationObjective = {
          sectionId: englishVariation.sectionId,
          objectiveId,
          base_lang_code: missingBaseLangCode,
          targ_lang_code: targLangCode,
          level: englishVariation.sectionId,
          lang_pair_word_id: langPairWordId,
          legacy_word_id: word,
          legacy_vocab_identifier: word,
          wordId: word,
          baseLanguage: missingBaseLangCode,
          targetLanguage: targLangCode,
          domain: englishVariation.domain,
          proficiencyLevel: englishVariation.proficiencyLevel,
          difficulty: englishVariation.difficulty,
          objectiveBaseText,
          objectiveTargetText,
          createdAt: new Date().toISOString(),
        };

        newObjectives.push(newObjective);
      }

      // Save all new objectives
      setGeneralStatusMessage(`Saving ${newObjectives.length} new variations...`);
      for (const objective of newObjectives) {
        await createConversationObjective(objective);
      }

      setGeneralStatusMessage(
        `Successfully generated ${newObjectives.length} missing language variation(s)!`
      );

      // Refetch objectives to show updated list
      try {
        const updatedObjectives = await fetchConversationObjectives(section);
        setExistingObjectives(updatedObjectives);
      } catch (fetchErr: any) {
        console.error("Failed to refetch objectives after generating missing languages:", fetchErr);
      }

      setTimeout(() => setGeneralStatusMessage(null), 3000);
    } catch (err: any) {
      console.error("Error generating missing languages:", err);
      setGeneralStatusMessage(`Error: ${err.message || "Unknown error"}`);
    } finally {
      setGeneratingMissingLanguages(false);
    }
  };

  // --- Handler to open delete all modal ---
  const handleOpenDeleteAllModal = () => {
    if (existingObjectives.length === 0) {
      setGeneralStatusMessage("No objectives to delete.");
      return;
    }
    setDeleteAllConfirmText("");
    setIsDeleteAllModalOpen(true);
  };

  // --- Handler to close delete all modal ---
  const handleCloseDeleteAllModal = () => {
    setIsDeleteAllModalOpen(false);
    setDeleteAllConfirmText("");
  };

  // --- Handler to delete all objectives for the section ---
  const handleDeleteAllObjectives = async () => {
    if (deleteAllConfirmText.toLowerCase() !== "delete") {
      return;
    }

    setDeletingAllObjectives(true);
    setGeneralStatusMessage(`Deleting all ${existingObjectives.length} objectives...`);

    try {
      let deletedCount = 0;
      const totalCount = existingObjectives.length;

      for (const objective of existingObjectives) {
        await deleteConversationObjective(objective.sectionId, objective.objectiveId);
        deletedCount++;
        setGeneralStatusMessage(`Deleting objectives... (${deletedCount}/${totalCount})`);
      }

      setGeneralStatusMessage(
        `Successfully deleted all ${deletedCount} objectives for "${section}".`
      );
      setExistingObjectives([]);
      handleCloseDeleteAllModal();

      setTimeout(() => setGeneralStatusMessage(null), 3000);
    } catch (err: any) {
      console.error("Error deleting all objectives:", err);
      setGeneralStatusMessage(`Error deleting objectives: ${err.message}`);

      // Refetch to show current state
      try {
        const updatedObjectives = await fetchConversationObjectives(section);
        setExistingObjectives(updatedObjectives);
      } catch (fetchErr: any) {
        console.error("Failed to refetch objectives:", fetchErr);
      }
    } finally {
      setDeletingAllObjectives(false);
    }
  };

  // --- Handler to generate all missing languages for entire section ---
  const handleGenerateAllMissingLanguages = async () => {
    if (groupedObjectives.length === 0) {
      setGeneralStatusMessage("No objectives to process.");
      return;
    }

    setGeneratingAllMissing(true);
    setGeneralStatusMessage("Analyzing section for missing language variations...");

    try {
      const targLangCode = langCodeMap[sourceLanguage];
      if (!targLangCode) {
        throw new Error("Could not determine target language code");
      }

      // Calculate total missing variations across all groups
      let totalMissing = 0;
      const groupsWithMissing: {
        group: (typeof groupedObjectives)[0];
        missingBaseLangs: string[];
      }[] = [];

      for (const group of groupedObjectives) {
        const existingBaseLangs = new Set(
          group.variations.map((v) => v.base_lang_code || v.baseLanguage)
        );
        const missingBaseLangs = AVAILABLE_LANGUAGES.map((lang) => langCodeMap[lang]).filter(
          (langCode) => langCode !== targLangCode && !existingBaseLangs.has(langCode)
        );

        if (missingBaseLangs.length > 0) {
          totalMissing += missingBaseLangs.length;
          groupsWithMissing.push({ group, missingBaseLangs });
        }
      }

      if (totalMissing === 0) {
        setGeneralStatusMessage("All language variations already exist for all objectives!");
        setGeneratingAllMissing(false);
        return;
      }

      setGeneralStatusMessage(
        `Found ${totalMissing} missing variations across ${groupsWithMissing.length} objectives. Generating...`
      );

      let generatedCount = 0;
      const allNewObjectives: ConversationObjective[] = [];

      // Process each group with missing languages
      for (const { group, missingBaseLangs } of groupsWithMissing) {
        // Use English version as the base, or the first available variation
        const englishVariation =
          group.variations.find((v) => (v.base_lang_code || v.baseLanguage) === "EN") ||
          group.variations[0];

        const word =
          extractWordFromLangPairId(englishVariation.lang_pair_word_id) ||
          englishVariation.legacy_word_id ||
          englishVariation.wordId;

        // Generate objectives for each missing base language
        for (const missingBaseLangCode of missingBaseLangs) {
          const missingBaseLangName =
            Object.keys(langCodeMap).find((name) => langCodeMap[name] === missingBaseLangCode) ||
            missingBaseLangCode;

          setGeneralStatusMessage(
            `Generating ${missingBaseLangName} for "${group.word}"... (${generatedCount + 1}/${totalMissing})`
          );

          let objectiveBaseText = englishVariation.objectiveBaseText;
          const objectiveTargetText = englishVariation.objectiveTargetText;

          // If base language is not English, translate the base text
          if (missingBaseLangCode !== "EN") {
            try {
              const translationPayload = {
                messages: [
                  {
                    role: "system",
                    content: `You are a professional translator. Translate the given English text to ${missingBaseLangName}. Keep it short and direct. Return only the translation with no additional text or formatting.`,
                  },
                  {
                    role: "user",
                    content: `Translate this objective to ${missingBaseLangName}: "${englishVariation.objectiveBaseText}"`,
                  },
                ],
                temperature: 0.3,
                max_tokens: 100,
              };

              const translationResponse = await callOpenAI(translationPayload);

              let translatedText: string;
              if (typeof translationResponse?.response === "string") {
                translatedText = translationResponse.response.trim();
              } else if (
                typeof translationResponse?.response === "object" &&
                translationResponse?.response !== null
              ) {
                translatedText = String(translationResponse.response).trim();
              } else {
                translatedText = englishVariation.objectiveBaseText;
              }

              objectiveBaseText = translatedText || englishVariation.objectiveBaseText;
            } catch (translationErr: any) {
              console.error(
                `Failed to translate objective for ${missingBaseLangName}:`,
                translationErr
              );
              objectiveBaseText = englishVariation.objectiveBaseText;
            }
          }

          const objectiveId = generateUUID();
          const langPairWordId = constructLangPairWordId(targLangCode, missingBaseLangCode, word);

          const newObjective: ConversationObjective = {
            sectionId: englishVariation.sectionId,
            objectiveId,
            base_lang_code: missingBaseLangCode,
            targ_lang_code: targLangCode,
            level: englishVariation.sectionId,
            lang_pair_word_id: langPairWordId,
            legacy_word_id: word,
            legacy_vocab_identifier: word,
            wordId: word,
            baseLanguage: missingBaseLangCode,
            targetLanguage: targLangCode,
            domain: englishVariation.domain,
            proficiencyLevel: englishVariation.proficiencyLevel,
            difficulty: englishVariation.difficulty,
            objectiveBaseText,
            objectiveTargetText,
            createdAt: new Date().toISOString(),
          };

          allNewObjectives.push(newObjective);
          generatedCount++;
        }
      }

      // Save all new objectives
      setGeneralStatusMessage(`Saving ${allNewObjectives.length} new variations...`);
      let savedCount = 0;
      for (const objective of allNewObjectives) {
        await createConversationObjective(objective);
        savedCount++;
        if (savedCount % 5 === 0) {
          setGeneralStatusMessage(`Saving... (${savedCount}/${allNewObjectives.length})`);
        }
      }

      setGeneralStatusMessage(
        `Successfully generated ${allNewObjectives.length} missing language variation(s)!`
      );

      // Refetch objectives to show updated list
      try {
        const updatedObjectives = await fetchConversationObjectives(section);
        setExistingObjectives(updatedObjectives);
      } catch (fetchErr: any) {
        console.error("Failed to refetch objectives after generating all missing:", fetchErr);
      }

      setTimeout(() => setGeneralStatusMessage(null), 3000);
    } catch (err: any) {
      console.error("Error generating all missing languages:", err);
      setGeneralStatusMessage(`Error: ${err.message || "Unknown error"}`);
    } finally {
      setGeneratingAllMissing(false);
    }
  };

  // --- ObjectiveCarousel Component ---
  const ObjectiveCarousel: React.FC<{
    variations: ConversationObjective[];
    onEdit: (objective: ConversationObjective) => void;
    onDelete: (objective: ConversationObjective, allVariations: ConversationObjective[]) => void;
    onAIEdit: (objective: ConversationObjective) => void;
    onGenerateMissing: (variations: ConversationObjective[]) => void;
    isGenerating?: boolean;
  }> = ({ variations, onEdit, onDelete, onAIEdit, onGenerateMissing, isGenerating = false }) => {
    // Find initial index: prioritize "EN"
    const initialIndex = useMemo(() => {
      if (!variations || variations.length === 0) return 0;
      const enIndex = variations.findIndex((v) => {
        const baseLang = v.base_lang_code || v.baseLanguage;
        return baseLang === "EN";
      });
      return enIndex !== -1 ? enIndex : 0;
    }, [variations]);

    const [currentIndex, setCurrentIndex] = useState<number>(initialIndex);

    // Reset index when variations change
    useEffect(() => {
      setCurrentIndex(initialIndex);
    }, [initialIndex]);

    const handleCarouselChange = (direction: "prev" | "next") => {
      if (!variations || variations.length <= 1) return;
      setCurrentIndex((prev) => {
        const newIndex =
          direction === "next"
            ? (prev + 1) % variations.length
            : (prev - 1 + variations.length) % variations.length;
        return newIndex;
      });
    };

    if (!variations || variations.length === 0) {
      return <div className={styles.carousel}>No variations.</div>;
    }

    const currentVariation = currentIndex < variations.length ? variations[currentIndex] : null;

    if (!currentVariation) {
      return <div className={styles.carousel}>Error loading variation.</div>;
    }

    const baseLang = currentVariation.base_lang_code || currentVariation.baseLanguage;
    const targLang = currentVariation.targ_lang_code || currentVariation.targetLanguage;

    // Calculate missing languages
    const existingBaseLangs = new Set(variations.map((v) => v.base_lang_code || v.baseLanguage));
    const totalPossibleLangs = AVAILABLE_LANGUAGES.length - 1; // Exclude target language
    const missingCount = totalPossibleLangs - variations.length;
    const hasMissingLanguages = missingCount > 0;

    return (
      <div className={styles.carousel}>
        <button
          onClick={() => handleCarouselChange("prev")}
          className={styles.carouselButton}
          disabled={variations.length <= 1}
          aria-label="Previous variation"
        >
          ←
        </button>
        <div className={styles.carouselContent}>
          <div style={{ marginBottom: "10px" }}>
            <strong>Base Language ({baseLang}):</strong>
            <div style={{ marginTop: "5px", fontSize: "16px" }}>
              {currentVariation.objectiveBaseText}
            </div>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <strong>Target Language ({targLang}):</strong>
            <div style={{ marginTop: "5px", fontSize: "16px", color: "#1976d2" }}>
              {currentVariation.objectiveTargetText}
            </div>
          </div>
          <div style={{ fontSize: "0.85em", color: "#666" }}>
            <div>
              <strong>Objective ID:</strong> {currentVariation.objectiveId.substring(0, 8)}...
            </div>
            <div>
              <strong>Section:</strong> {currentVariation.sectionId}
            </div>
            <div>
              <strong>Domain:</strong> {currentVariation.domain}
            </div>
            <div>
              <strong>Proficiency:</strong> {currentVariation.proficiencyLevel}
            </div>
            <div>
              <strong>Difficulty:</strong> {currentVariation.difficulty}
            </div>
            <div title={currentVariation.lang_pair_word_id}>
              <strong>Lang Pair ID:</strong>{" "}
              {currentVariation.lang_pair_word_id
                ? currentVariation.lang_pair_word_id.length > 30
                  ? currentVariation.lang_pair_word_id.substring(0, 30) + "..."
                  : currentVariation.lang_pair_word_id
                : "N/A"}
            </div>
            <div>
              <strong>Created:</strong> {new Date(currentVariation.createdAt).toLocaleDateString()}
            </div>
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "5px", flexWrap: "wrap" }}>
            <button
              onClick={() => onEdit(currentVariation)}
              className={`${styles.button} ${styles.editButton}`}
            >
              Edit
            </button>
            <button
              onClick={() => onAIEdit(currentVariation)}
              className={`${styles.button} ${styles.actionButton}`}
              style={{ backgroundColor: "#4a90e2" }}
            >
              🤖 AI Edit
            </button>
            <button
              onClick={() => onDelete(currentVariation, variations)}
              className={`${styles.button} ${styles.deleteButton}`}
            >
              Delete
            </button>
            {hasMissingLanguages && (
              <button
                onClick={() => onGenerateMissing(variations)}
                className={`${styles.button} ${styles.actionButton}`}
                style={{ backgroundColor: "#ff9800", color: "white" }}
                title={`Generate ${missingCount} missing language variation(s)`}
                disabled={isGenerating}
              >
                {isGenerating ? "⏳ Generating..." : `➕ Generate Missing (${missingCount})`}
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => handleCarouselChange("next")}
          className={styles.carouselButton}
          disabled={variations.length <= 1}
          aria-label="Next variation"
        >
          →
        </button>
      </div>
    );
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Conversation Objectives Manager</h1>
      {error && <div className={styles.error}>{error}</div>}
      {generalStatusMessage && <div className={styles.infoMessage}>{generalStatusMessage}</div>}

      {/* --- Shared Controls: Language / Section / Proficiency / Difficulty --- */}
      <div className={styles.controlsGrid}>
        <div className={styles.formControl}>
          <label htmlFor="sourceLanguage" className={styles.label}>
            Source Language:
          </label>
          <select
            id="sourceLanguage"
            value={sourceLanguage}
            onChange={handleSourceLanguageChange}
            className={styles.input}
            disabled={loadingSections}
          >
            <option value="">Select Language</option>
            {AVAILABLE_LANGUAGES.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="section" className={styles.label}>
            Section:
          </label>
          <select
            id="section"
            value={section}
            onChange={handleSectionChange}
            className={styles.input}
            disabled={!sourceLanguage || loadingSections}
          >
            <option value="">
              {loadingSections
                ? "Loading Sections..."
                : sourceLanguage
                  ? "Select Section"
                  : "Choose Language First"}
            </option>
            {!loadingSections &&
              courseSections.map((cs) => (
                <option key={cs.identifier} value={cs.sectionNameForAPI}>
                  {cs.sectionNameForAPI} (ID: {cs.identifier})
                </option>
              ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="proficiencyLevel" className={styles.label}>
            Proficiency Level:
          </label>
          <select
            id="proficiencyLevel"
            value={proficiencyLevel}
            onChange={handleProficiencyChange}
            className={styles.input}
          >
            {PROFICIENCY_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="difficulty" className={styles.label}>
            Difficulty (1-3):
          </label>
          <select
            id="difficulty"
            value={difficulty}
            onChange={handleDifficultyChange}
            className={styles.input}
          >
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </div>
        <div className={styles.formControl}>
          <label htmlFor="domain" className={styles.label}>
            Domain:
          </label>
          <select id="domain" value={domain} onChange={handleDomainChange} className={styles.input}>
            {DOMAINS.map((d) => (
              <option key={d} value={d}>
                {d.charAt(0).toUpperCase() + d.slice(1)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!loadingSections && sourceLanguage && courseSections.length === 0 && !error && (
        <p className={styles.infoMessage}>No course sections found for {sourceLanguage}.</p>
      )}

      {/* --- Vocabulary Selection --- */}
      {section && vocabularyWords.length > 0 && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>Select Vocabulary Words for AI Generation</h2>
          {loadingVocabulary ? (
            <p className={styles.loadingMessage}>Loading vocabulary...</p>
          ) : (
            <>
              <div className={styles.tableContainer}>
                <table className={styles.vocabTable}>
                  <thead>
                    <tr>
                      <th style={{ width: "60px" }}>Select</th>
                      <th>Vocabulary Word</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vocabularyWords.map((word, index) => (
                      <tr key={index}>
                        <td style={{ textAlign: "center" }}>
                          <input
                            type="checkbox"
                            checked={selectedWordsBucket.has(word)}
                            onChange={() => toggleWord(word)}
                            disabled={generatingObjectives}
                          />
                        </td>
                        <td>{word}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedWordsBucket.size > 0 && (
                <div className={styles.buttonContainer}>
                  <button
                    onClick={handleGenerateAIObjectives}
                    className={`${styles.button} ${styles.actionButton}`}
                    disabled={generatingObjectives || !selectedWordsBucket.size}
                  >
                    {generatingObjectives
                      ? "Generating AI Objectives..."
                      : `Generate AI Objectives (${selectedWordsBucket.size} words)`}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {section && vocabularyWords.length === 0 && !loadingVocabulary && (
        <div className={styles.sectionContainer}>
          <p className={styles.infoMessage}>No vocabulary words found for this section.</p>
        </div>
      )}

      {/* --- AI Generated Objectives Preview --- */}
      {aiGeneratedObjectives && (
        <div className={styles.sectionContainer}>
          <h2 className={styles.subHeading}>Review AI-Generated Objectives</h2>
          <p className={styles.infoMessage}>
            Please review the generated objectives below. You can approve to save them all or
            discard them.
          </p>

          <div className={styles.tableContainer}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Word ID</th>
                  <th>Base Language</th>
                  <th>Target Language</th>
                  <th>Objective (Base)</th>
                  <th>Objective (Target)</th>
                  <th>Domain</th>
                  <th>Proficiency</th>
                  <th>Difficulty</th>
                </tr>
              </thead>
              <tbody>
                {aiGeneratedObjectives.map((objective, index) => (
                  <tr key={index}>
                    <td>{objective.wordId || "N/A"}</td>
                    <td>{(objective.base_lang_code || objective.baseLanguage)?.toUpperCase()}</td>
                    <td>{(objective.targ_lang_code || objective.targetLanguage)?.toUpperCase()}</td>
                    <td>{objective.objectiveBaseText}</td>
                    <td>{objective.objectiveTargetText}</td>
                    <td>{objective.domain}</td>
                    <td>{objective.proficiencyLevel}</td>
                    <td>{objective.difficulty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            className={styles.buttonContainer}
            style={{ justifyContent: "center", gap: "15px", marginTop: "20px" }}
          >
            <button
              onClick={handleApproveAiObjectives}
              className={`${styles.button} ${styles.saveButton}`}
              disabled={savingAiObjectives}
            >
              {savingAiObjectives
                ? "Saving Objectives..."
                : `Approve & Save All (${aiGeneratedObjectives.length})`}
            </button>
            <button
              onClick={handleRejectAiObjectives}
              className={`${styles.button} ${styles.cancelButton}`}
              disabled={savingAiObjectives}
            >
              Discard All
            </button>
          </div>
        </div>
      )}

      {/* --- Existing Conversation Objectives --- */}
      {section && (
        <div className={styles.sectionContainer}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "15px",
            }}
          >
            <h2 className={styles.subHeading} style={{ marginBottom: 0 }}>
              Existing Conversation Objectives for "{section}"
            </h2>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
              <button
                onClick={handleOpenCreateModal}
                className={`${styles.button} ${styles.actionButton}`}
                disabled={!section || !sourceLanguage}
              >
                Add New Objective
              </button>
              {existingObjectives.length > 0 && (
                <>
                  <button
                    onClick={handleGenerateAllMissingLanguages}
                    className={`${styles.button} ${styles.actionButton}`}
                    style={{ backgroundColor: "#ff9800" }}
                    disabled={generatingAllMissing || generatingMissingLanguages}
                  >
                    {generatingAllMissing ? "Generating..." : "Generate All Missing Languages"}
                  </button>
                  <button
                    onClick={handleOpenDeleteAllModal}
                    className={`${styles.button} ${styles.deleteButton}`}
                    disabled={deletingAllObjectives}
                  >
                    Delete All Objectives
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Base Language Filter */}
          {existingObjectives.length > 0 && (
            <div className={styles.filterContainer} style={{ marginBottom: "20px" }}>
              <div className={styles.formControl}>
                <label htmlFor="baseLanguageFilter" className={styles.label}>
                  Filter by Base Language:
                </label>
                <select
                  id="baseLanguageFilter"
                  value={baseLanguageFilter}
                  onChange={handleBaseLanguageFilterChange}
                  className={styles.input}
                  style={{ maxWidth: "300px" }}
                >
                  <option value="">All Base Languages</option>
                  {Array.from(
                    new Set(
                      existingObjectives.map(
                        (obj) => obj.base_lang_code || obj.baseLanguage // Support both field names
                      )
                    )
                  )
                    .filter(Boolean)
                    .sort()
                    .map((baseLang) => (
                      <option key={baseLang} value={baseLang!}>
                        {baseLang!.toUpperCase()}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}
          {loadingExistingObjectives && (
            <p className={styles.loadingMessage}>Loading existing objectives...</p>
          )}
          {existingObjectivesError && <div className={styles.error}>{existingObjectivesError}</div>}
          {!loadingExistingObjectives &&
            !existingObjectivesError &&
            existingObjectives.length === 0 && (
              <p className={styles.infoMessage}>
                No existing conversation objectives found for this section.
              </p>
            )}
          {!loadingExistingObjectives &&
            !existingObjectivesError &&
            filteredObjectives.length === 0 &&
            existingObjectives.length > 0 && (
              <p className={styles.infoMessage}>
                No objectives found for the selected base language filter.
              </p>
            )}
          {!loadingExistingObjectives &&
            !existingObjectivesError &&
            groupedObjectives.length > 0 && (
              <div className={styles.tableContainer}>
                <table className={styles.dataTable}>
                  <thead>
                    <tr>
                      <th>Word / Phrase</th>
                      <th>Base Language Variations</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groupedObjectives.map((group, index) => (
                      <tr key={`group-${index}`}>
                        <td style={{ verticalAlign: "top", maxWidth: "300px" }}>
                          <div
                            style={{ fontWeight: "bold", marginBottom: "5px", fontSize: "16px" }}
                          >
                            {group.word}
                          </div>
                          <div style={{ fontSize: "0.85em", color: "#666" }}>
                            {group.variations.length} variation
                            {group.variations.length !== 1 ? "s" : ""}
                          </div>
                        </td>
                        <td>
                          <ObjectiveCarousel
                            variations={group.variations}
                            onEdit={handleOpenEditModal}
                            onDelete={handleDeleteExistingObjective}
                            onAIEdit={handleOpenAIEdit}
                            onGenerateMissing={handleGenerateMissingLanguages}
                            isGenerating={generatingMissingLanguages}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* --- Edit Modal --- */}
      {isEditModalOpen && editingObjective && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <h2>Edit Conversation Objective (ID: {editingObjective.objectiveId})</h2>
            <div className={styles.modalForm}>
              <div className={styles.formControlModal}>
                <label htmlFor="objectiveBaseText">Objective (Base Language):</label>
                <textarea
                  id="objectiveBaseText"
                  name="objectiveBaseText"
                  value={editedObjectiveData.objectiveBaseText || ""}
                  onChange={handleEditFormChange}
                  rows={3}
                  className={styles.input}
                />
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="objectiveTargetText">Objective (Target Language):</label>
                <textarea
                  id="objectiveTargetText"
                  name="objectiveTargetText"
                  value={editedObjectiveData.objectiveTargetText || ""}
                  onChange={handleEditFormChange}
                  rows={3}
                  className={styles.input}
                />
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="wordId">Word ID (Optional):</label>
                <input
                  type="text"
                  id="wordId"
                  name="wordId"
                  value={editedObjectiveData.wordId || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                />
                <small style={{ display: "block", marginTop: "4px", color: "#666" }}>
                  Current Lang Pair Word ID: {editingObjective?.lang_pair_word_id || "Not set"}
                </small>
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="domain">Domain:</label>
                <select
                  id="domain"
                  name="domain"
                  value={editedObjectiveData.domain || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                >
                  <option value="">Select Domain</option>
                  {DOMAINS.map((d) => (
                    <option key={d} value={d}>
                      {d.charAt(0).toUpperCase() + d.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="proficiencyLevel">Proficiency Level:</label>
                <select
                  id="proficiencyLevel"
                  name="proficiencyLevel"
                  value={editedObjectiveData.proficiencyLevel || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                >
                  <option value="">Select Proficiency Level</option>
                  {PROFICIENCY_LEVELS.map((level) => (
                    <option key={level} value={level}>
                      {level}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="difficulty">Difficulty:</label>
                <select
                  id="difficulty"
                  name="difficulty"
                  value={editedObjectiveData.difficulty || ""}
                  onChange={handleEditFormChange}
                  className={styles.input}
                >
                  <option value="">Select Difficulty</option>
                  {DIFFICULTIES.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={handleUpdateEditedObjective}
                className={`${styles.button} ${styles.saveButton}`}
              >
                Save Changes
              </button>
              <button
                onClick={handleCloseEditModal}
                className={`${styles.button} ${styles.cancelButton}`}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* --- Create Modal --- */}
      {isCreateModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <h2>Create New Conversation Objective</h2>
            <div className={styles.modalForm}>
              <div className={styles.formControlModal}>
                <label htmlFor="createObjectiveBaseText">Objective (Base Language):</label>
                <textarea
                  id="createObjectiveBaseText"
                  name="objectiveBaseText"
                  value={newObjectiveData.objectiveBaseText || ""}
                  onChange={handleCreateFormChange}
                  rows={3}
                  className={styles.input}
                  placeholder="Enter the objective in English..."
                />
              </div>
              <div className={styles.formControlModal}>
                <label htmlFor="createObjectiveTargetText">Objective (Target Language):</label>
                <textarea
                  id="createObjectiveTargetText"
                  name="objectiveTargetText"
                  value={newObjectiveData.objectiveTargetText || ""}
                  onChange={handleCreateFormChange}
                  rows={3}
                  className={styles.input}
                  placeholder={`Enter the objective in ${sourceLanguage}...`}
                />
              </div>
              <div className={styles.formControlModal}>
                <label>Associated Vocabulary Words (Optional):</label>
                {vocabularyWords.length > 0 ? (
                  <div
                    style={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "10px",
                      backgroundColor: "#f9f9f9",
                    }}
                  >
                    {vocabularyWords.map((word, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                          padding: "4px",
                        }}
                      >
                        <input
                          type="checkbox"
                          id={`createWord_${index}`}
                          checked={createModalSelectedWords.has(word)}
                          onChange={() => toggleCreateModalWord(word)}
                          disabled={creatingObjective}
                          style={{ marginRight: "8px" }}
                        />
                        <label
                          htmlFor={`createWord_${index}`}
                          style={{
                            cursor: "pointer",
                            fontSize: "0.9em",
                            margin: 0,
                          }}
                        >
                          {word}
                        </label>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p
                    style={{
                      fontStyle: "italic",
                      color: "#666",
                      margin: "10px 0",
                      fontSize: "0.9em",
                    }}
                  >
                    No vocabulary words available for this section.
                  </p>
                )}
                {createModalSelectedWords.size > 0 && (
                  <p
                    style={{
                      marginTop: "8px",
                      fontSize: "0.85em",
                      color: "#555",
                    }}
                  >
                    Selected: {Array.from(createModalSelectedWords).join(", ")}
                  </p>
                )}
              </div>
              <div className={styles.formControlModal}>
                <label>Current Settings:</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "10px",
                    padding: "10px",
                    backgroundColor: "#f5f5f5",
                    borderRadius: "4px",
                  }}
                >
                  <div>
                    <strong>Section:</strong> {section}
                  </div>
                  <div>
                    <strong>Language:</strong> {sourceLanguage}
                  </div>
                  <div>
                    <strong>Domain:</strong> {domain}
                  </div>
                  <div>
                    <strong>Proficiency:</strong> {proficiencyLevel}
                  </div>
                  <div>
                    <strong>Difficulty:</strong> {difficulty}
                  </div>
                </div>
                <small>
                  These settings will be used for the new objective. Change them in the main form if
                  needed.
                </small>
              </div>
            </div>
            <div className={styles.modalActions}>
              <button
                onClick={handleCreateNewObjective}
                className={`${styles.button} ${styles.saveButton}`}
                disabled={creatingObjective}
              >
                {creatingObjective ? "Creating..." : "Create Objective"}
              </button>
              <button
                onClick={handleCloseCreateModal}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={creatingObjective}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── AI Editing Modal ── */}
      {showAIEditModal && editingObjective && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxWidth: "700px" }}>
            <h2>🤖 AI Objective Editor</h2>

            {/* Current Objective Display */}
            <div
              className={styles.modalSection}
              style={{
                backgroundColor: "#e3f2fd",
                border: "2px solid #2196f3",
                borderRadius: "6px",
                padding: "12px 15px",
                marginBottom: "15px",
              }}
            >
              <h4 style={{ margin: "0 0 8px 0", color: "#1565c0", fontSize: "14px" }}>
                📝 Current Objective
              </h4>
              <div style={{ marginBottom: "8px" }}>
                <strong>
                  Base Language ({editingObjective.base_lang_code || editingObjective.baseLanguage}
                  ):
                </strong>{" "}
                {editingObjective.objectiveBaseText}
              </div>
              <div style={{ marginBottom: "8px" }}>
                <strong>
                  Target Language (
                  {editingObjective.targ_lang_code || editingObjective.targetLanguage}):
                </strong>{" "}
                {editingObjective.objectiveTargetText}
              </div>
              <div style={{ marginBottom: "8px" }}>
                <strong>Word/Phrase:</strong>{" "}
                {extractWordFromLangPairId(editingObjective.lang_pair_word_id) ||
                  editingObjective.wordId ||
                  "N/A"}
              </div>
              <div style={{ fontSize: "13px", color: "#555", marginTop: "8px" }}>
                <span style={{ marginRight: "15px" }}>
                  <strong>Domain:</strong> {editingObjective.domain}
                </span>
                <span style={{ marginRight: "15px" }}>
                  <strong>Level:</strong> {editingObjective.proficiencyLevel}
                </span>
                <span>
                  <strong>Difficulty:</strong> {editingObjective.difficulty}
                </span>
              </div>
            </div>

            <div className={styles.modalSection}>
              <p style={{ marginBottom: "15px", color: "#666" }}>
                Describe how you want to modify this objective. The AI will generate a new version
                based on your instructions.
              </p>

              <div style={{ marginBottom: "20px" }}>
                <h4>Example Instructions:</h4>
                <ul style={{ color: "#888", fontSize: "14px", lineHeight: "1.6" }}>
                  <li>"Make it more formal"</li>
                  <li>"Change to use 'she/her' instead"</li>
                  <li>"Make it a question"</li>
                  <li>"Simplify for beginners"</li>
                  <li>"Make it more polite"</li>
                  <li>"Change the context to a restaurant"</li>
                </ul>
              </div>

              <label className={styles.label}>
                Modification Instructions:
                <textarea
                  value={aiEditPrompt}
                  onChange={(e) => setAIEditPrompt(e.target.value)}
                  placeholder="Describe how you want to modify this objective..."
                  className={styles.input}
                  rows={4}
                  disabled={isProcessingAIEdit}
                  style={{ width: "100%", resize: "vertical", marginTop: "8px" }}
                />
              </label>
            </div>

            {/* AI Edit Preview */}
            {aiEditPreview &&
              (() => {
                // Calculate number of variations for this objective
                const word =
                  extractWordFromLangPairId(editingObjective.lang_pair_word_id) ||
                  editingObjective.legacy_word_id ||
                  editingObjective.wordId ||
                  "Unknown";

                const variations = existingObjectives.filter((obj) => {
                  const objWord =
                    extractWordFromLangPairId(obj.lang_pair_word_id) ||
                    obj.legacy_word_id ||
                    obj.wordId;
                  return objWord === word;
                });

                const variationCount = variations.length;

                return (
                  <div
                    className={styles.modalSection}
                    style={{
                      backgroundColor: "#f0f8ff",
                      padding: "15px",
                      borderRadius: "8px",
                      marginTop: "15px",
                    }}
                  >
                    <h3 style={{ marginTop: "0", color: "#1565c0" }}>
                      Preview Modified Objectives (All Languages):
                    </h3>

                    {/* Display all language variations */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: "10px",
                        marginBottom: "15px",
                      }}
                    >
                      {aiEditPreview.map((preview, index) => {
                        const baseLang = preview.base_lang_code || preview.baseLanguage;
                        const targLang = preview.targ_lang_code || preview.targetLanguage;

                        return (
                          <div
                            key={index}
                            style={{
                              padding: "12px",
                              backgroundColor: "white",
                              borderRadius: "6px",
                              border: "2px solid #e0e0e0",
                            }}
                          >
                            <div
                              style={{
                                fontWeight: "bold",
                                marginBottom: "8px",
                                color: "#1976d2",
                                fontSize: "14px",
                              }}
                            >
                              {baseLang} → {targLang}
                            </div>
                            <div style={{ marginBottom: "6px" }}>
                              <span style={{ fontWeight: "bold", fontSize: "13px" }}>
                                Base ({baseLang}):
                              </span>
                              <span style={{ marginLeft: "8px", color: "#2e7d32" }}>
                                {preview.objectiveBaseText}
                              </span>
                            </div>
                            <div>
                              <span style={{ fontWeight: "bold", fontSize: "13px" }}>
                                Target ({targLang}):
                              </span>
                              <span style={{ marginLeft: "8px", color: "#e65100" }}>
                                {preview.objectiveTargetText}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Update All Variations Option */}
                    {variationCount > 1 && (
                      <div
                        style={{
                          marginTop: "15px",
                          padding: "12px",
                          backgroundColor: "#e1f5fe",
                          borderRadius: "6px",
                        }}
                      >
                        <h4 style={{ marginTop: "0", marginBottom: "10px", color: "#01579b" }}>
                          Apply Changes To:
                        </h4>
                        <div style={{ fontSize: "14px", color: "#555", marginBottom: "12px" }}>
                          This objective has{" "}
                          <strong>{variationCount} base language variation(s)</strong>. Choose how
                          to apply these changes:
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              padding: "8px",
                              backgroundColor: "white",
                              borderRadius: "4px",
                              border: updateAllVariations ? "2px solid #2196f3" : "2px solid #ddd",
                            }}
                          >
                            <input
                              type="radio"
                              name="updateScope"
                              checked={updateAllVariations}
                              onChange={() => setUpdateAllVariations(true)}
                              style={{ marginRight: "10px" }}
                            />
                            <div>
                              <strong>Update All {variationCount} Variations</strong>
                              <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>
                                Apply these changes to all base language versions of this objective
                              </div>
                            </div>
                          </label>
                          <label
                            style={{
                              display: "flex",
                              alignItems: "center",
                              cursor: "pointer",
                              padding: "8px",
                              backgroundColor: "white",
                              borderRadius: "4px",
                              border: !updateAllVariations ? "2px solid #2196f3" : "2px solid #ddd",
                            }}
                          >
                            <input
                              type="radio"
                              name="updateScope"
                              checked={!updateAllVariations}
                              onChange={() => setUpdateAllVariations(false)}
                              style={{ marginRight: "10px" }}
                            />
                            <div>
                              <strong>
                                Update Only Current (
                                {editingObjective.base_lang_code || editingObjective.baseLanguage})
                              </strong>
                              <div style={{ fontSize: "13px", color: "#666", marginTop: "2px" }}>
                                Apply changes only to this specific variation
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    )}

                    <div
                      style={{
                        marginTop: "15px",
                        padding: "10px",
                        backgroundColor: "#fff3cd",
                        borderRadius: "4px",
                        fontSize: "14px",
                      }}
                    >
                      <strong>Note:</strong> Click "Apply Changes" to save the modified objectives
                      to the database, or "Regenerate" to try again with different instructions.
                    </div>
                  </div>
                );
              })()}

            {/* Modal Actions */}
            <div className={styles.modalActions}>
              {!aiEditPreview ? (
                <>
                  <button
                    onClick={handleProcessAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    disabled={isProcessingAIEdit || !aiEditPrompt.trim()}
                    style={{ backgroundColor: "#4a90e2" }}
                  >
                    {isProcessingAIEdit ? "Generating..." : "Generate Preview"}
                  </button>
                  <button
                    onClick={handleCloseAIEdit}
                    className={styles.button}
                    disabled={isProcessingAIEdit}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleApplyAIEdit}
                    className={`${styles.button} ${styles.saveButton}`}
                    style={{ backgroundColor: "#4caf50" }}
                  >
                    Apply Changes
                  </button>
                  <button
                    onClick={() => {
                      setAIEditPreview(null);
                      setAIEditPrompt("");
                    }}
                    className={`${styles.button}`}
                    style={{ backgroundColor: "#ff9800" }}
                  >
                    Regenerate
                  </button>
                  <button onClick={handleCloseAIEdit} className={styles.button}>
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- Delete All Confirmation Modal --- */}
      {isDeleteAllModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent} style={{ maxWidth: "500px" }}>
            <h2 style={{ color: "#d32f2f", marginTop: 0 }}>Delete All Objectives</h2>

            <div className={styles.deleteAllWarning}>
              <p style={{ marginBottom: "15px", fontWeight: "bold" }}>
                You are about to delete ALL {existingObjectives.length} objectives for "{section}".
              </p>
              <p style={{ marginBottom: "20px", color: "#666" }}>
                This action cannot be undone. All conversation objectives for this section will be
                permanently removed.
              </p>

              <div className={styles.formControlModal}>
                <label
                  htmlFor="deleteConfirmInput"
                  style={{ display: "block", marginBottom: "8px" }}
                >
                  Type <strong style={{ color: "#d32f2f" }}>delete</strong> to confirm:
                </label>
                <input
                  type="text"
                  id="deleteConfirmInput"
                  value={deleteAllConfirmText}
                  onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                  placeholder="Type 'delete' to confirm"
                  className={styles.input}
                  disabled={deletingAllObjectives}
                  autoComplete="off"
                  style={{
                    borderColor:
                      deleteAllConfirmText.toLowerCase() === "delete" ? "#4caf50" : "#ccc",
                  }}
                />
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                onClick={handleDeleteAllObjectives}
                className={`${styles.button} ${styles.deleteButton}`}
                disabled={deleteAllConfirmText.toLowerCase() !== "delete" || deletingAllObjectives}
                style={{
                  opacity: deleteAllConfirmText.toLowerCase() !== "delete" ? 0.5 : 1,
                  padding: "12px 25px",
                }}
              >
                {deletingAllObjectives
                  ? "Deleting..."
                  : `Delete All ${existingObjectives.length} Objectives`}
              </button>
              <button
                onClick={handleCloseDeleteAllModal}
                className={`${styles.button} ${styles.cancelButton}`}
                disabled={deletingAllObjectives}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationObjectives;
